import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Select, AutoComplete, Button, Spin, Tooltip, Table } from 'antd';
import { FilterOutlined, ClearOutlined, ReloadOutlined, PoweroffOutlined } from '@ant-design/icons';
import vegaEmbed from 'vega-embed';
import { executeDashboardSQL } from '../services/dashboardService';

// ── SQL utilities (shared with DashboardPanel) ─────────────────────
//
// WHERE-filter parsing is built on a proper SQL tokenizer rather than
// raw regex over the SQL text. The tokenizer handles:
//   • single-quoted string literals ('...' with '' as escape)
//   • double-quoted identifiers ("..." — Snowflake/ANSI; NOT a string)
//   • line and block comments
//   • paren-depth tracking
//
// Filter recognition then runs on the token stream, so:
//   • 'WHERE'/'LIMIT'/etc. inside a string never affect parsing
//   • IN (SELECT ...) is correctly skipped (not parsed as a literal list)
//   • IN ('a, b', 'c') keeps the comma inside the literal
//   • Each filter records the exact byte span of its value(s) in the
//     original SQL, so rewrite is surgical (no unintended substitutions
//     in CASE expressions, select-list, subqueries, etc.).
//
// Recognized predicate shapes (column on the LEFT only):
//   col = lit                     col != lit / col <> lit
//   col > lit / col >= lit / col < lit / col <= lit
//   col IN (lit, lit, ...)        col BETWEEN lit AND lit
//   col LIKE 'pat' / col ILIKE 'pat'
//   col IS NULL / col IS NOT NULL (read-only chips)
//   col >|>=|<|<= DATEADD(unit, n, <base>)   -- base = any function call
//   FUNC(col) <op> ...            -- LOWER/UPPER/TRIM wrappers
//   col = TRUE / col = FALSE
//
// Predicates that contain a top-level OR are skipped (no chip), because
// the chip UI cannot represent "rewrite one branch of an OR".

const KEYWORDS_END_WHERE = new Set([
  'GROUP', 'ORDER', 'LIMIT', 'HAVING', 'UNION', 'INTERSECT', 'EXCEPT',
  'WINDOW', 'QUALIFY', 'OFFSET', 'FETCH', 'CONNECT', 'CLUSTER',
]);
const SKIP_COLS = new Set(['RN']);              // window-function row-numbers
const SKIP_WRAPPER_FUNCS = new Set([
  'COUNT', 'SUM', 'AVG', 'MIN', 'MAX', 'EXISTS', 'NOT',
  'COALESCE', 'NVL', 'IFF', 'CASE', 'IF', 'CAST', 'CONVERT',
]);

/** Tokenize a SQL string. Returns a flat list of tokens with start/end offsets. */
function tokenizeSql(sql) {
  const tokens = [];
  let i = 0;
  const n = sql.length;
  while (i < n) {
    const c = sql[i];
    // whitespace
    if (c === ' ' || c === '\t' || c === '\n' || c === '\r' || c === '\f' || c === '\v') {
      let j = i + 1;
      while (j < n && /\s/.test(sql[j])) j++;
      tokens.push({ type: 'ws', value: sql.slice(i, j), start: i, end: j });
      i = j; continue;
    }
    // line comment
    if (c === '-' && sql[i + 1] === '-') {
      let j = i + 2;
      while (j < n && sql[j] !== '\n') j++;
      tokens.push({ type: 'comment', value: sql.slice(i, j), start: i, end: j });
      i = j; continue;
    }
    // block comment
    if (c === '/' && sql[i + 1] === '*') {
      let j = i + 2;
      while (j < n - 1 && !(sql[j] === '*' && sql[j + 1] === '/')) j++;
      j = Math.min(n, j + 2);
      tokens.push({ type: 'comment', value: sql.slice(i, j), start: i, end: j });
      i = j; continue;
    }
    // string literal: '...' with '' as escape
    if (c === "'") {
      let j = i + 1; let val = '';
      while (j < n) {
        if (sql[j] === "'" && sql[j + 1] === "'") { val += "'"; j += 2; continue; }
        if (sql[j] === "'") { j++; break; }
        val += sql[j]; j++;
      }
      tokens.push({ type: 'string', value: val, start: i, end: j });
      i = j; continue;
    }
    // double-quoted identifier: "..." with "" as escape (NOT a string literal)
    if (c === '"') {
      let j = i + 1; let val = '';
      while (j < n) {
        if (sql[j] === '"' && sql[j + 1] === '"') { val += '"'; j += 2; continue; }
        if (sql[j] === '"') { j++; break; }
        val += sql[j]; j++;
      }
      tokens.push({ type: 'qident', value: val, start: i, end: j });
      i = j; continue;
    }
    // number
    if ((c >= '0' && c <= '9') || (c === '.' && sql[i + 1] >= '0' && sql[i + 1] <= '9')) {
      let j = i;
      while (j < n && /[0-9.]/.test(sql[j])) j++;
      if (sql[j] === 'e' || sql[j] === 'E') {
        j++;
        if (sql[j] === '+' || sql[j] === '-') j++;
        while (j < n && /[0-9]/.test(sql[j])) j++;
      }
      tokens.push({ type: 'number', value: sql.slice(i, j), start: i, end: j });
      i = j; continue;
    }
    // identifier / keyword
    if ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_') {
      let j = i;
      while (j < n && /[A-Za-z0-9_$]/.test(sql[j])) j++;
      tokens.push({ type: 'word', value: sql.slice(i, j), start: i, end: j });
      i = j; continue;
    }
    // multi-char operators
    const two = sql.slice(i, i + 2);
    if (two === '>=' || two === '<=' || two === '<>' || two === '!=' || two === '||') {
      tokens.push({ type: 'op', value: two, start: i, end: i + 2 });
      i += 2; continue;
    }
    tokens.push({ type: 'punct', value: c, start: i, end: i + 1 });
    i++;
  }
  return tokens;
}

const isTrivia = (t) => t.type === 'ws' || t.type === 'comment';
const isKw = (t, ...names) => !!(t && t.type === 'word' && names.includes(t.value.toUpperCase()));

/**
 * Find every WHERE clause body in the token stream. For each WHERE, collect
 * the trailing condition tokens until we either close the enclosing scope
 * or hit a clause-ending keyword (GROUP/ORDER/LIMIT/...) at the same paren
 * depth. Returns array of { tokens } with non-trivia tokens only.
 */
function findWhereClauses(tokens) {
  const code = tokens.filter(t => !isTrivia(t));
  const out = [];
  let depth = 0;
  for (let i = 0; i < code.length; i++) {
    const t = code[i];
    if (t.type === 'punct' && t.value === '(') depth++;
    else if (t.type === 'punct' && t.value === ')') depth--;
    else if (t.type === 'word' && t.value.toUpperCase() === 'WHERE') {
      const start = i + 1;
      let j = start;
      let d = depth;
      while (j < code.length) {
        const tj = code[j];
        if (tj.type === 'punct' && tj.value === '(') d++;
        else if (tj.type === 'punct' && tj.value === ')') {
          if (d === depth) break; // closing the enclosing scope
          d--;
        } else if (d === depth && tj.type === 'word' && KEYWORDS_END_WHERE.has(tj.value.toUpperCase())) {
          break;
        }
        j++;
      }
      out.push({ tokens: code.slice(start, j) });
      // Resume scanning *inside* the captured body so nested WHEREs
      // (e.g. inside an IN-subquery) are still discovered.
      i = start - 1;
    }
  }
  return out;
}

/**
 * Split a WHERE-body slice on top-level AND. Groups that contain a top-level
 * OR are dropped (not chip-able). Parenthesized sub-expressions are kept whole.
 */
function splitTopLevelAnds(tokens) {
  const groups = [];
  let cur = []; let depth = 0; let hasOr = false;
  // Track pending BETWEEN clauses — `BETWEEN x AND y` contains a top-level
  // AND that must NOT split the predicate. When we see BETWEEN at depth 0,
  // bump the counter; the next top-level AND is consumed (kept inline)
  // instead of flushing a new group.
  let pendingBetweenAnds = 0;
  const flush = () => { if (cur.length) groups.push({ tokens: cur, hasOr }); cur = []; hasOr = false; };
  for (const t of tokens) {
    if (t.type === 'punct' && t.value === '(') { depth++; cur.push(t); continue; }
    if (t.type === 'punct' && t.value === ')') { depth--; cur.push(t); continue; }
    if (depth === 0 && t.type === 'word') {
      const u = t.value.toUpperCase();
      if (u === 'BETWEEN') { pendingBetweenAnds++; cur.push(t); continue; }
      if (u === 'AND') {
        if (pendingBetweenAnds > 0) { pendingBetweenAnds--; cur.push(t); continue; }
        flush();
        continue;
      }
      if (u === 'OR') { hasOr = true; cur.push(t); continue; }
    }
    cur.push(t);
  }
  flush();
  return groups.filter(g => !g.hasOr);
}

/** Read a column reference (`col` or `qual.col`). Returns null on failure. */
function readColumnRef(tokens, idx) {
  let i = idx;
  const t0 = tokens[i];
  if (!t0 || (t0.type !== 'word' && t0.type !== 'qident')) return null;
  const parts = [t0.type === 'qident' ? `"${t0.value.replace(/"/g, '""')}"` : t0.value];
  i++;
  if (tokens[i] && tokens[i].type === 'punct' && tokens[i].value === '.') {
    i++;
    const t2 = tokens[i];
    if (!t2 || (t2.type !== 'word' && t2.type !== 'qident')) return null;
    parts.push('.', t2.type === 'qident' ? `"${t2.value.replace(/"/g, '""')}"` : t2.value);
    i++;
  }
  const last = parts[parts.length - 1];
  const name = last.startsWith('"') ? last.slice(1, -1) : last;
  // A bare keyword is not a column
  if (parts.length === 1 && /^(AND|OR|NOT|NULL|IS|LIKE|ILIKE|BETWEEN|IN|EXISTS|CASE|WHEN|THEN|ELSE|END|TRUE|FALSE|SELECT|FROM)$/i.test(name)) return null;
  return { column: name.toUpperCase(), originalColumn: parts.join(''), consumed: i - idx };
}

/** Read a literal (string / number / TRUE / FALSE / NULL), with optional unary +/-. */
function readLiteral(tokens, idx) {
  let i = idx; let sign = '';
  if (tokens[i] && tokens[i].type === 'punct' && (tokens[i].value === '-' || tokens[i].value === '+')) {
    sign = tokens[i].value === '-' ? '-' : '';
    i++;
  }
  const t = tokens[i];
  if (!t) return null;
  if (t.type === 'string') {
    return { value: t.value, isString: true, start: t.start, end: t.end, consumed: i - idx + 1 };
  }
  if (t.type === 'number') {
    return { value: sign + t.value, isString: false, start: tokens[idx].start, end: t.end, consumed: i - idx + 1 };
  }
  if (t.type === 'word') {
    const u = t.value.toUpperCase();
    if (u === 'TRUE' || u === 'FALSE') {
      return { value: u, isString: false, start: t.start, end: t.end, consumed: i - idx + 1, isBool: true };
    }
    if (u === 'NULL') {
      return { value: null, isString: false, start: t.start, end: t.end, consumed: i - idx + 1, isNull: true };
    }
  }
  return null;
}

// Wrappers we'll peel off the RHS of a predicate so the chip can recognize
// patterns like `UPPER(col) = UPPER('val')` or `col LIKE LOWER('foo%')`.
// The returned span still points at the inner literal only, so
// rebuildSqlWithFilters rewrites just the literal and preserves the wrapping
// function call.
const PEELABLE_RHS_FUNCS = new Set(['UPPER', 'LOWER', 'TRIM']);

/**
 * Like readLiteral, but transparently peels a single layer of UPPER/LOWER/TRIM
 * around a literal. Used for the RHS of binary predicates so the chip can
 * recognize symmetric wrappers (e.g. `UPPER(col) = UPPER('val')`).
 *
 * NOT used for IN (...) lists: the IN rewrite replaces a span covering all
 * items, which would corrupt SQL like `IN (UPPER('a'), UPPER('b'))`.
 */
function readMaybeWrappedLiteral(tokens, idx) {
  const t = tokens[idx];
  if (t && t.type === 'word' && PEELABLE_RHS_FUNCS.has(t.value.toUpperCase()) &&
      tokens[idx + 1]?.type === 'punct' && tokens[idx + 1].value === '(') {
    const inner = readLiteral(tokens, idx + 2);
    if (!inner) return null;
    const close = tokens[idx + 2 + inner.consumed];
    if (!close || close.type !== 'punct' || close.value !== ')') return null;
    // Span stays on the inner literal; consumed covers FUNC ( inner ).
    return { ...inner, consumed: 2 + inner.consumed + 1 };
  }
  return readLiteral(tokens, idx);
}

/** Recognize a single predicate; returns array of filter defs (0 or 1). */
function recognizePredicate(tokens) {
  if (tokens.length === 0) return [];

  // Optional FUNC( col ) wrapper
  let columnRef = null;
  let wrapperFunc = null;
  let i = 0;
  const t0 = tokens[0];
  if (t0 && t0.type === 'word' && tokens[1]?.type === 'punct' && tokens[1].value === '(') {
    const fname = t0.value.toUpperCase();
    if (!SKIP_WRAPPER_FUNCS.has(fname)) {
      const tryCol = readColumnRef(tokens, 2);
      if (tryCol) {
        const after = 2 + tryCol.consumed;
        if (tokens[after]?.type === 'punct' && tokens[after].value === ')') {
          wrapperFunc = t0.value;
          columnRef = tryCol;
          i = after + 1;
        }
      }
    }
  }
  if (!columnRef) {
    const tryCol = readColumnRef(tokens, 0);
    if (!tryCol) return [];
    columnRef = tryCol;
    i = tryCol.consumed;
  }
  if (SKIP_COLS.has(columnRef.column)) return [];

  const opTok = tokens[i];
  if (!opTok) return [];

  const base = { column: columnRef.column, originalColumn: columnRef.originalColumn, wrapperFunc };

  // = (eq, bool, or any literal)
  if (opTok.type === 'punct' && opTok.value === '=') {
    const lit = readMaybeWrappedLiteral(tokens, i + 1);
    if (!lit || lit.isNull) return [];
    if (lit.isBool) {
      return [{ ...base, operator: '=', type: 'bool', values: [String(lit.value)], isString: false, valueSpan: { start: lit.start, end: lit.end } }];
    }
    return [{ ...base, operator: '=', type: 'eq', values: [String(lit.value)], isString: lit.isString, valueSpan: { start: lit.start, end: lit.end } }];
  }
  // != / <>
  if (opTok.type === 'op' && (opTok.value === '!=' || opTok.value === '<>')) {
    const lit = readMaybeWrappedLiteral(tokens, i + 1);
    if (!lit || lit.isNull || lit.isBool) return [];
    return [{ ...base, operator: opTok.value, type: 'neq', negated: true, values: [String(lit.value)], isString: lit.isString, valueSpan: { start: lit.start, end: lit.end } }];
  }
  // NOT IN / NOT LIKE / NOT ILIKE / NOT BETWEEN  — same shapes as their
  // positive counterparts, just flagged `negated:true` so the renderer
  // can show a red "NOT" badge. Span still points at the literal(s) so
  // rebuildSqlWithFilters preserves the leading "NOT " bytes verbatim.
  if (opTok.type === 'word' && opTok.value.toUpperCase() === 'NOT') {
    const next = tokens[i + 1];
    const nextU = next && next.type === 'word' ? next.value.toUpperCase() : null;
    // NOT IN (...)
    if (nextU === 'IN') {
      const open = tokens[i + 2];
      if (!open || open.type !== 'punct' || open.value !== '(') return [];
      let j = i + 3;
      let depth = 1;
      const items = [];
      let isString = null;
      let abort = false;
      while (j < tokens.length && depth > 0) {
        const t = tokens[j];
        if (t.type === 'punct' && t.value === '(') { depth++; j++; continue; }
        if (t.type === 'punct' && t.value === ')') { depth--; if (depth === 0) break; j++; continue; }
        if (depth === 1 && t.type === 'punct' && t.value === ',') { j++; continue; }
        if (depth === 1) {
          if (t.type === 'word' && /^(SELECT|WITH|VALUES)$/i.test(t.value)) { abort = true; break; }
          const lit = readLiteral(tokens, j);
          if (!lit || lit.isNull || lit.isBool) { abort = true; break; }
          if (isString === null) isString = lit.isString;
          else if (lit.isString !== isString) { abort = true; break; }
          items.push(lit);
          j += lit.consumed;
          continue;
        }
        j++;
      }
      if (abort || items.length === 0) return [];
      return [{
        ...base,
        operator: 'NOT IN',
        type: 'in',
        negated: true,
        values: items.map(it => String(it.value)),
        isString: !!isString,
        valueSpan: { start: items[0].start, end: items[items.length - 1].end },
      }];
    }
    // NOT LIKE / NOT ILIKE
    if (nextU === 'LIKE' || nextU === 'ILIKE') {
      const lit = readMaybeWrappedLiteral(tokens, i + 2);
      if (!lit || !lit.isString) return [];
      return [{ ...base, operator: `NOT ${nextU}`, type: 'like', negated: true, values: [String(lit.value)], isString: true, valueSpan: { start: lit.start, end: lit.end } }];
    }
    // NOT BETWEEN x AND y
    if (nextU === 'BETWEEN') {
      const lit1 = readMaybeWrappedLiteral(tokens, i + 2);
      if (!lit1 || lit1.isNull || lit1.isBool) return [];
      const andT = tokens[i + 2 + lit1.consumed];
      if (!isKw(andT, 'AND')) return [];
      const lit2 = readMaybeWrappedLiteral(tokens, i + 3 + lit1.consumed);
      if (!lit2 || lit2.isNull || lit2.isBool) return [];
      if (lit1.isString !== lit2.isString) return [];
      return [{
        ...base,
        operator: 'NOT BETWEEN',
        type: 'between',
        negated: true,
        values: [String(lit1.value), String(lit2.value)],
        isString: lit1.isString,
        valueSpan: { start: lit1.start, end: lit2.end },
        _subSpans: [{ start: lit1.start, end: lit1.end }, { start: lit2.start, end: lit2.end }],
      }];
    }
    return [];
  }
  // > / >= / < / <=  (with DATEADD special case)
  const isLtGt = (opTok.type === 'op' && (opTok.value === '>=' || opTok.value === '<=')) ||
                 (opTok.type === 'punct' && (opTok.value === '>' || opTok.value === '<'));
  if (isLtGt) {
    const op = opTok.value;
    const next = tokens[i + 1];
    if (next?.type === 'word' && next.value.toUpperCase() === 'DATEADD' &&
        tokens[i + 2]?.type === 'punct' && tokens[i + 2].value === '(') {
      // DATEADD(<unit>, <n>, <base-expr>)  — base-expr is opaque (any token sequence)
      const unitT = tokens[i + 3];
      const c1 = tokens[i + 4];
      let nIdx = i + 5;
      let nSign = '';
      if (tokens[nIdx]?.type === 'punct' && (tokens[nIdx].value === '-' || tokens[nIdx].value === '+')) {
        nSign = tokens[nIdx].value === '-' ? '-' : '';
        nIdx++;
      }
      const numT = tokens[nIdx];
      const c2 = tokens[nIdx + 1];
      if (unitT?.type === 'word' && c1?.type === 'punct' && c1.value === ',' &&
          numT?.type === 'number' && c2?.type === 'punct' && c2.value === ',') {
        const startPos = nSign ? tokens[i + 5].start : numT.start;
        return [{
          ...base,
          operator: op,
          type: 'dateadd',
          values: [nSign + numT.value],
          isString: false,
          dateaddUnit: unitT.value,
          dateaddOp: op,
          valueSpan: { start: startPos, end: numT.end },
        }];
      }
    }
    const lit = readMaybeWrappedLiteral(tokens, i + 1);
    if (!lit || lit.isNull || lit.isBool) return [];
    return [{ ...base, operator: op, type: 'cmp', values: [String(lit.value)], isString: lit.isString, valueSpan: { start: lit.start, end: lit.end } }];
  }
  // IN (...)
  if (opTok.type === 'word' && opTok.value.toUpperCase() === 'IN') {
    const open = tokens[i + 1];
    if (!open || open.type !== 'punct' || open.value !== '(') return [];
    let j = i + 2;
    let depth = 1;
    const items = [];
    let isString = null;
    let abort = false;
    while (j < tokens.length && depth > 0) {
      const t = tokens[j];
      if (t.type === 'punct' && t.value === '(') { depth++; j++; continue; }
      if (t.type === 'punct' && t.value === ')') { depth--; if (depth === 0) break; j++; continue; }
      if (depth === 1 && t.type === 'punct' && t.value === ',') { j++; continue; }
      if (depth === 1) {
        // Subquery? Any non-literal? Then we don't chip this filter.
        if (t.type === 'word' && /^(SELECT|WITH|VALUES)$/i.test(t.value)) { abort = true; break; }
        const lit = readLiteral(tokens, j);
        if (!lit || lit.isNull || lit.isBool) { abort = true; break; }
        if (isString === null) isString = lit.isString;
        else if (lit.isString !== isString) { abort = true; break; }
        items.push(lit);
        j += lit.consumed;
        continue;
      }
      j++;
    }
    if (abort || items.length === 0) return [];
    return [{
      ...base,
      operator: 'IN',
      type: 'in',
      values: items.map(it => String(it.value)),
      isString: !!isString,
      valueSpan: { start: items[0].start, end: items[items.length - 1].end },
    }];
  }
  // BETWEEN x AND y
  if (opTok.type === 'word' && opTok.value.toUpperCase() === 'BETWEEN') {
    const lit1 = readMaybeWrappedLiteral(tokens, i + 1);
    if (!lit1 || lit1.isNull || lit1.isBool) return [];
    const andT = tokens[i + 1 + lit1.consumed];
    if (!isKw(andT, 'AND')) return [];
    const lit2 = readMaybeWrappedLiteral(tokens, i + 2 + lit1.consumed);
    if (!lit2 || lit2.isNull || lit2.isBool) return [];
    if (lit1.isString !== lit2.isString) return [];
    return [{
      ...base,
      operator: 'BETWEEN',
      type: 'between',
      values: [String(lit1.value), String(lit2.value)],
      isString: lit1.isString,
      valueSpan: { start: lit1.start, end: lit2.end },
      _subSpans: [{ start: lit1.start, end: lit1.end }, { start: lit2.start, end: lit2.end }],
    }];
  }
  // LIKE / ILIKE
  if (opTok.type === 'word' && (opTok.value.toUpperCase() === 'LIKE' || opTok.value.toUpperCase() === 'ILIKE')) {
    const lit = readMaybeWrappedLiteral(tokens, i + 1);
    if (!lit || !lit.isString) return [];
    return [{ ...base, operator: opTok.value.toUpperCase(), type: 'like', values: [String(lit.value)], isString: true, valueSpan: { start: lit.start, end: lit.end } }];
  }
  // IS NULL / IS NOT NULL  (read-only chips)
  if (opTok.type === 'word' && opTok.value.toUpperCase() === 'IS') {
    const next = tokens[i + 1];
    if (isKw(next, 'NULL')) {
      return [{ ...base, operator: 'IS NULL', type: 'isnull', values: [], isString: false, readonly: true }];
    }
    if (isKw(next, 'NOT') && isKw(tokens[i + 2], 'NULL')) {
      return [{ ...base, operator: 'IS NOT NULL', type: 'isnotnull', values: [], isString: false, readonly: true }];
    }
  }
  return [];
}

/**
 * Parse WHERE conditions from a SQL query. Returns an object keyed by a
 * stable filter id (column name; subsequent matches for the same column
 * become COL#2, COL#3, ...). Each value is a filter definition with:
 *   { id, column, originalColumn, operator, type, values, isString,
 *     wrapperFunc?, dateaddUnit?, dateaddOp?, valueSpan?, _subSpans?,
 *     readonly?, displayLabel }
 *
 * `valueSpan` is a {start, end} byte range into the ORIGINAL SQL string,
 * pointing at the literal(s) we want to rewrite. rebuildSqlWithFilters
 * uses these spans to do precise edits — we never rewrite anything outside
 * a recognized WHERE-clause literal.
 */
export function parseWhereFilters(sql) {
  if (!sql) return {};
  const tokens = tokenizeSql(sql);
  const wheres = findWhereClauses(tokens);
  const out = {};
  const counter = {};
  // Track per-column occurrence so duplicate columns can be disambiguated
  // visually (e.g. "STATUS 1" / "STATUS 2") in a second pass below.
  const ordinal = {};
  for (const w of wheres) {
    const groups = splitTopLevelAnds(w.tokens);
    for (const g of groups) {
      const defs = recognizePredicate(g.tokens);
      // Span covering the full predicate in the ORIGINAL SQL — used by
      // rebuildSqlWithFilters when a filter is disabled (predicate gets
      // replaced with `1=1` so the surrounding ANDs stay valid).
      const predicateSpan = g.tokens.length > 0
        ? { start: g.tokens[0].start, end: g.tokens[g.tokens.length - 1].end }
        : null;
      for (const d of defs) {
        const seen = counter[d.column] || 0;
        counter[d.column] = seen + 1;
        const id = seen === 0 ? d.column : `${d.column}#${seen + 1}`;
        const opLabel = (d.operator && d.operator !== '=' && d.operator !== 'IN') ? ` ${d.operator}` : '';
        out[id] = { ...d, id, displayLabel: `${d.column}${opLabel}`, predicateSpan };
        ordinal[id] = seen + 1;
      }
    }
  }
  // Disambiguate columns that appear more than once: append " 1" / " 2" /…
  // before the operator suffix so the user can tell the chips apart.
  for (const id of Object.keys(out)) {
    const def = out[id];
    if ((counter[def.column] || 0) > 1) {
      const opLabel = (def.operator && def.operator !== '=' && def.operator !== 'IN') ? ` ${def.operator}` : '';
      def.displayLabel = `${def.column} ${ordinal[id]}${opLabel}`;
    }
  }
  return out;
}

/**
 * Rebuild SQL by surgically replacing each filter's value span with the
 * user's selection. Spans are processed in descending order so earlier
 * replacements don't shift later offsets. Filters whose values are
 * unchanged are skipped (the original SQL bytes are preserved).
 *
 * When a filter id appears in `disabledFilters` (truthy), its entire
 * predicate span is replaced with `1=1` so the surrounding AND
 * connectors stay valid. Disabled filters ignore their value entirely.
 */
export function rebuildSqlWithFilters(sql, parsedFilters, filterValues, disabledFilters) {
  if (!sql) return sql;
  const ops = [];
  const quote = (v, isStr) => isStr ? `'${String(v).replace(/'/g, "''")}'` : String(v);
  const isDisabled = (id) => !!(disabledFilters && (disabledFilters[id] === true || (typeof disabledFilters.has === 'function' && disabledFilters.has(id))));

  for (const [id, def] of Object.entries(parsedFilters)) {
    if (isDisabled(id)) {
      // Replace the entire predicate with `1=1`. Skips read-only
      // predicates: a user can't toggle them, and the chip row never
      // surfaces a switch for them.
      if (def.readonly) continue;
      if (def.predicateSpan) {
        ops.push({ start: def.predicateSpan.start, end: def.predicateSpan.end, replacement: '1=1' });
      }
      continue;
    }
    if (def.readonly) continue;
    const vals = filterValues?.[id];
    if (!vals || vals.length === 0) continue;
    // Skip if value matches the originally-parsed value (preserve original bytes)
    const sortedNew = JSON.stringify([...vals].map(String).sort());
    const sortedOrig = JSON.stringify([...def.values].map(String).sort());
    if (sortedNew === sortedOrig) continue;

    if (def.type === 'in' && def.valueSpan) {
      const list = vals.map(v => quote(v, def.isString)).join(', ');
      ops.push({ start: def.valueSpan.start, end: def.valueSpan.end, replacement: list });
    } else if (def.type === 'between' && def._subSpans && vals.length >= 2) {
      ops.push({ start: def._subSpans[0].start, end: def._subSpans[0].end, replacement: quote(vals[0], def.isString) });
      ops.push({ start: def._subSpans[1].start, end: def._subSpans[1].end, replacement: quote(vals[1], def.isString) });
    } else if (def.type === 'bool' && def.valueSpan) {
      ops.push({ start: def.valueSpan.start, end: def.valueSpan.end, replacement: String(vals[0]).toUpperCase() === 'TRUE' ? 'TRUE' : 'FALSE' });
    } else if (def.type === 'dateadd' && def.valueSpan) {
      ops.push({ start: def.valueSpan.start, end: def.valueSpan.end, replacement: String(vals[0]) });
    } else if (def.valueSpan) {
      // eq, neq, cmp, like
      ops.push({ start: def.valueSpan.start, end: def.valueSpan.end, replacement: quote(vals[0], def.isString) });
    }
  }

  ops.sort((a, b) => b.start - a.start);
  let out = sql;
  for (const op of ops) out = out.slice(0, op.start) + op.replacement + out.slice(op.end);
  return out;
}

/**
 * Shared WHERE filter bar that re-fetches data from the backend when a filter value changes.
 * Used by both chat delivery components and (via ComponentFilterBar) by DashboardPanel.
 *
 * Props:
 *  sqlQuery              – original SQL string
 *  data                  – currently loaded { columns, rows } (for building option lists)
 *  onData                – (newData) => void  called after a successful re-fetch
 *  onLoading             – (bool) => void
 *  externalFilterValues  – (optional) controlled filter state from parent
 *  onFilterValuesChange  – (optional) callback when filter values change (for syncing across tabs)
 */
// ── Shared filter-bar UI primitives ────────────────────────────────
// WhereFilterChips renders the chip row used by SqlWhereFilterBar (chat
// delivery components) and ComponentFilterBar (dashboard cards). The
// surrounding component owns refetch behavior; this is a pure renderer.
//
// Props:
//   parsedFilters     – output of parseWhereFilters(sql)
//   filterValues      – { [filterId]: string[] } current values
//   data              – { columns, rows } for option suggestions
//   onFilterChange    – (filterId, vals) => void
//   onReset           – () => void
//   className         – container class
//   labelClassName    – optional class for the leading WHERE label
//   error             – optional error message string
function buildOptions(def, data) {
  if (def.type === 'dateadd') {
    const presets = ['-1', '-7', '-14', '-30', '-60', '-90'];
    const allVals = new Set([...presets, ...def.values.map(String)]);
    return [...allVals].sort((a, b) => Number(a) - Number(b)).map(v => {
      const abs = Math.abs(Number(v));
      return { label: `Last ${abs} day${abs !== 1 ? 's' : ''}`, value: v };
    });
  }
  if (def.type === 'bool') {
    return [{ label: 'TRUE', value: 'TRUE' }, { label: 'FALSE', value: 'FALSE' }];
  }
  const optSet = new Set(def.values.map(String));
  if (data?.columns && data?.rows) {
    const colIdx = data.columns.findIndex(c => c.toUpperCase() === def.column);
    if (colIdx >= 0) {
      for (const row of data.rows) {
        if (optSet.size >= 200) break;
        if (row[colIdx] != null) optSet.add(String(row[colIdx]));
      }
    }
  }
  return [...optSet].sort((a, b) => {
    const na = Number(a), nb = Number(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    return a.localeCompare(b);
  }).map(v => ({ label: v, value: v }));
}

function valuesEqual(a, b) {
  if (!a || !b) return a === b;
  if (a.length !== b.length) return false;
  const sa = [...a].map(String).sort();
  const sb = [...b].map(String).sort();
  for (let i = 0; i < sa.length; i++) if (sa[i] !== sb[i]) return false;
  return true;
}

export function WhereFilterChips({ parsedFilters, filterValues, data, onFilterChange, onReset, disabledFilters, onToggleDisabled, className = 'sql-where-filter-bar', labelClassName = 'sql-where-filter-label', error = null }) {
  const filterEntries = Object.entries(parsedFilters);
  const isDisabled = (id) => !!(disabledFilters && (disabledFilters[id] === true || (typeof disabledFilters.has === 'function' && disabledFilters.has(id))));
  // Per-field inline warning (auto-clears after ~2.5s) — used in place of
  // a global toast which is too wide for the dense filter row.
  const [warnings, setWarnings] = useState({});
  const warnFor = useCallback((id, msg) => {
    setWarnings(prev => ({ ...prev, [id]: msg }));
    setTimeout(() => setWarnings(prev => {
      if (prev[id] !== msg) return prev;
      const next = { ...prev }; delete next[id]; return next;
    }), 2500);
  }, []);
  // Hide the chip row entirely if there's nothing the user can edit \u2014
  // read-only predicates (IS NULL / NOT NULL / opaque) are excluded from
  // rendering, so a SQL whose WHERE only contains those shouldn't show
  // an empty bar. BETWEEN is editable (rendered as two inputs).
  const hasEditableEntries = filterEntries.some(([, def]) => !def.readonly);
  if (filterEntries.length === 0 || !hasEditableEntries) return null;

  const hasChanges = filterEntries.some(([id, def]) => {
    if (def.readonly) return false;
    if (isDisabled(id)) return true;
    const current = filterValues?.[id] || def.values;
    return !valuesEqual(current, def.values);
  });

  // Renders one chip. Read-only filters (IS NULL / IS NOT NULL / opaque
  // shapes) are hidden from the chip row \u2014 the operator itself fully
  // expresses the predicate, and there's nothing the user can edit. They
  // remain visible in the SQL modal. BETWEEN is rendered with two inputs.
  const renderOne = (id, def) => {
    if (def.readonly) return null;
    const disabled = isDisabled(id);
    const opts = buildOptions(def, data);
    const currentVal = filterValues?.[id] || def.values;
    // Required-input marker: WHERE filters cannot be empty (the SQL would
    // be invalid). The `*` advertises this; clearing a value reverts to
    // the original parsed value and surfaces an inline warning.
    const requiredMarker = (
      <Tooltip title={`This column is filtered via SQL WHERE (${def.type}) — a value is required.`}>
        <span style={{ marginLeft: 3, color: 'var(--dx-warn, #faad14)', cursor: 'help' }}>*</span>
      </Tooltip>
    );
    const toggleBtn = null;
    const wrapperStyle = {
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      opacity: disabled ? 0.55 : 1,
    };
    const labelEl = (
      <span style={{ fontSize: 10, color: 'var(--theme-primary, #1890ff)', fontWeight: 600, lineHeight: 1.2, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ textDecoration: disabled ? 'line-through' : 'none' }}>{def.displayLabel}</span>
        {!disabled && requiredMarker}
      </span>
    );
    // BETWEEN/NOT BETWEEN \u2192 two AutoComplete inputs (lo, hi).
    if (def.type === 'between') {
      const lo = currentVal[0] != null ? String(currentVal[0]) : '';
      const hi = currentVal[1] != null ? String(currentVal[1]) : '';
      const handleBetween = (idx, v) => {
        if (v == null || v === '') {
          warnFor(id, 'Required — reverted to default');
          onFilterChange(id, def.values);
          return;
        }
        const next = [...currentVal];
        while (next.length < 2) next.push(def.values[next.length]);
        next[idx] = v;
        onFilterChange(id, next);
      };
      return (
        <div key={id} style={wrapperStyle}>
          {labelEl}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <AutoComplete
              value={lo}
              options={opts}
              onChange={(v) => handleBetween(0, v)}
              placeholder="from"
              size="small"
              style={{ minWidth: 80, maxWidth: 120 }}
              allowClear
              popupMatchSelectWidth={false}
              disabled={disabled}
            />
            <span style={{ fontSize: 10, color: 'var(--theme-textSecondary, #888)' }}>and</span>
            <AutoComplete
              value={hi}
              options={opts}
              onChange={(v) => handleBetween(1, v)}
              placeholder="to"
              size="small"
              style={{ minWidth: 80, maxWidth: 120 }}
              allowClear
              popupMatchSelectWidth={false}
              disabled={disabled}
            />
          </div>
          {warnings[id] && !disabled && (
            <span style={{ fontSize: 10, color: '#faad14', lineHeight: 1.2 }}>⚠ {warnings[id]}</span>
          )}
        </div>
      );
    }
    // Single-value WHERE columns (=, !=, <, >, like, dateadd) render as an
    // AutoComplete — lets the user type a fresh value AND pick from
    // observed values, without the visual clutter of multi-tag chips.
    const isSingleValue = def.type !== 'in' && def.type !== 'bool';
    if (isSingleValue) {
      return (
        <div key={id} style={wrapperStyle}>
          {labelEl}
          <AutoComplete
            value={currentVal[0] != null ? String(currentVal[0]) : ''}
            options={opts}
            onChange={(v) => {
              if (v == null || v === '') {
                warnFor(id, 'Required — reverted to default');
                onFilterChange(id, def.values);
                return;
              }
              onFilterChange(id, [v]);
            }}
            placeholder={`Type or pick ${def.column}…`}
            style={{ minWidth: 110, maxWidth: 220 }}
            size="small"
            allowClear
            popupMatchSelectWidth={false}
            disabled={disabled}
            filterOption={(input, option) =>
              String(option?.value ?? '').toLowerCase().includes(String(input).toLowerCase())
            }
          />
          {warnings[id] && !disabled && (
            <span style={{ fontSize: 10, color: '#faad14', lineHeight: 1.2 }}>⚠ {warnings[id]}</span>
          )}
        </div>
      );
    }
    const selectMode = def.type === 'bool' ? undefined : 'multiple';
    const selectValue = def.type === 'bool' ? currentVal[0] : currentVal;
    return (
      <div key={id} style={wrapperStyle}>
        {labelEl}
        <Select
          mode={selectMode}
          value={selectValue}
          options={opts}
          onChange={(v) => {
            if (def.type === 'bool') {
              onFilterChange(id, [v]);
            } else {
              const arr = Array.isArray(v) ? v : [v];
              if (arr.length === 0) {
                warnFor(id, 'Required — reverted to default');
                onFilterChange(id, def.values);
                return;
              }
              onFilterChange(id, arr);
            }
          }}
          style={{ minWidth: 110, maxWidth: 220 }}
          size="small"
          showSearch
          allowClear={false}
          popupMatchSelectWidth={false}
          maxTagCount={1}
          maxTagPlaceholder={(omitted) => `+${omitted.length}`}
          disabled={disabled}
        />
        {warnings[id] && !disabled && (
          <span style={{ fontSize: 10, color: '#faad14', lineHeight: 1.2 }}>⚠ {warnings[id]}</span>
        )}
      </div>
    );
  };

  return (
    <div className={className}>
      <span className={labelClassName}>
        <FilterOutlined style={{ fontSize: 10 }} /> WHERE
      </span>
      {filterEntries.map(([id, def]) => renderOne(id, def))}
      {hasChanges && (
        <Tooltip title="Reset to original SQL values">
          <Button size="small" type="text" icon={<ClearOutlined style={{ fontSize: 10 }} />}
            onClick={onReset}
            style={{ fontSize: 11, color: '#8c8c8c', alignSelf: 'flex-end', paddingBottom: 2 }} />
        </Tooltip>
      )}
      {error && (
        <span style={{ fontSize: 10, color: '#ff4d4f', alignSelf: 'center', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          title={error}>⚠ {error}</span>
      )}
    </div>
  );
}

export function SqlWhereFilterBar({ sqlQuery, data, onData, onLoading, externalFilterValues, onFilterValuesChange, externalDisabledFilters, onDisabledFiltersChange }) {
  const parsedFilters = useMemo(() => parseWhereFilters(sqlQuery), [sqlQuery]);
  // Lazy initializer reuses the memoized parsedFilters value (avoids the
  // double-parse footgun on first render).
  const [internalFilterValues, setInternalFilterValues] = useState(() =>
    Object.fromEntries(Object.entries(parsedFilters).map(([id, def]) => [id, def.values]))
  );
  const [internalDisabledFilters, setInternalDisabledFilters] = useState({});
  // Use external state if provided (controlled mode), otherwise use internal state
  const filterValues = externalFilterValues || internalFilterValues;
  const setFilterValues = onFilterValuesChange || setInternalFilterValues;
  const disabledFilters = externalDisabledFilters || internalDisabledFilters;
  const setDisabledFilters = onDisabledFiltersChange || setInternalDisabledFilters;
  const abortRef = useRef(null);

  const [filterError, setFilterError] = useState(null);

  // Use a ref to access current data without causing runQuery to re-create on every data change
  const dataRef = useRef(data);
  useEffect(() => { dataRef.current = data; }, [data]);

  const runQuery = useCallback((newFilterValues, newDisabledFilters) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const effectiveDisabled = newDisabledFilters !== undefined ? newDisabledFilters : disabledFilters;
    const newSql = rebuildSqlWithFilters(sqlQuery, parsedFilters, newFilterValues, effectiveDisabled);
    const sqlChanged = sqlQuery !== newSql;
    console.log('[SqlWhereFilterBar] filter changed:', { original: sqlQuery, rebuilt: newSql, changed: sqlChanged, filterValues: newFilterValues, disabledFilters: effectiveDisabled });
    setFilterError(null);
    if (onLoading) onLoading(true);
    // Only force a fresh fetch when the SQL actually changed; otherwise
    // let the backend serve the cached result for the original query.
    executeDashboardSQL(newSql, 200, sqlChanged, { signal: controller.signal })
      .then(res => {
        if (onData) {
          // Preserve column schema when query returns 0 rows (backend may return empty columns)
          const newData = res.data;
          const prevData = dataRef.current;
          if ((!newData.columns || newData.columns.length === 0) && prevData?.columns?.length > 0) {
            onData({ ...newData, columns: prevData.columns, rows: [] });
          } else {
            onData(newData);
          }
        }
        if (onLoading) onLoading(false);
      })
      .catch(err => {
        if (err.name !== 'AbortError') {
          console.error('[SqlWhereFilterBar] query failed:', String(err?.message || err).replace(/[\r\n]/g, ' '));
          setFilterError(err.message || 'Query failed');
          if (onLoading) onLoading(false);
        }
      });
  }, [sqlQuery, parsedFilters, onData, onLoading, disabledFilters]);

  // Reset when the sqlQuery changes (e.g. message updated) — only for internal (uncontrolled) mode
  useEffect(() => {
    if (!externalFilterValues) {
      const next = Object.fromEntries(Object.entries(parsedFilters).map(([id, def]) => [id, def.values]));
      setInternalFilterValues(next);
    }
    if (!externalDisabledFilters) {
      setInternalDisabledFilters({});
    }
  }, [parsedFilters, externalFilterValues, externalDisabledFilters]);

  // Re-execute query when external filter values change (e.g. filter changed on another tab)
  const prevExternalRef = useRef(null);
  const prevExternalDisabledRef = useRef(null);
  const justChangedLocallyRef = useRef(false);
  useEffect(() => {
    if (!externalFilterValues && !externalDisabledFilters) return;
    const prev = prevExternalRef.current;
    const prevDis = prevExternalDisabledRef.current;
    prevExternalRef.current = externalFilterValues;
    prevExternalDisabledRef.current = externalDisabledFilters;
    // Skip re-execution if this component initiated the change
    if (justChangedLocallyRef.current) {
      justChangedLocallyRef.current = false;
      return;
    }
    if (JSON.stringify(prev) !== JSON.stringify(externalFilterValues) ||
        JSON.stringify(prevDis) !== JSON.stringify(externalDisabledFilters)) {
      runQuery(externalFilterValues || filterValues, externalDisabledFilters || disabledFilters);
    }
  }, [externalFilterValues, externalDisabledFilters, runQuery, filterValues, disabledFilters]);

  const filterEntries = Object.entries(parsedFilters);
  if (filterEntries.length === 0) return null;

  const handleChange = (id, vals) => {
    const next = { ...filterValues, [id]: vals };
    setFilterValues(next);
    justChangedLocallyRef.current = true;
    runQuery(next, disabledFilters);
  };

  const handleToggleDisabled = (id, nextDisabled) => {
    const next = { ...disabledFilters };
    if (nextDisabled) next[id] = true; else delete next[id];
    setDisabledFilters(next);
    justChangedLocallyRef.current = true;
    runQuery(filterValues, next);
  };

  const handleReset = () => {
    const reset = Object.fromEntries(filterEntries.map(([id, def]) => [id, def.values]));
    setFilterValues(reset);
    setDisabledFilters({});
    justChangedLocallyRef.current = true;
    runQuery(reset, {});
  };

  return (
    <WhereFilterChips
      parsedFilters={parsedFilters}
      filterValues={filterValues}
      disabledFilters={disabledFilters}
      onToggleDisabled={handleToggleDisabled}
      data={data}
      onFilterChange={handleChange}
      onReset={handleReset}
      error={filterError}
    />
  );
}

/**
 * Compute unique values for each column index from table rows.
 * Returns { [colIdx]: string[] }
 */
const getTableColumnOptions = (columns, rows, maxValues = 100) => {
  const result = {};
  if (!columns || !rows) return result;
  columns.forEach((_, idx) => {
    const valueSet = new Set();
    for (const row of rows) {
      if (valueSet.size >= maxValues) break;
      valueSet.add(row[idx] != null ? String(row[idx]) : '(null)');
    }
    result[idx] = [...valueSet].sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });
  });
  return result;
};

/**
 * Extract field definitions from a Vega-Lite encoding spec (supports layers).
 * Returns [{ channel, field, type }]
 */
const extractChartFields = (spec) => {
  if (!spec) return [];
  const fields = [];
  const encoding = spec.encoding || {};

  const channels = ['x', 'y', 'color', 'size', 'shape', 'detail', 'opacity', 'row', 'column', 'facet', 'text', 'tooltip'];
  channels.forEach(channel => {
    const enc = encoding[channel];
    if (enc?.field && !fields.some(f => f.field === enc.field)) {
      fields.push({ channel, field: enc.field, type: enc.type });
    }
    if (Array.isArray(enc)) {
      enc.forEach(e => {
        if (e?.field && !fields.some(f => f.field === e.field)) {
          fields.push({ channel, field: e.field, type: e.type });
        }
      });
    }
  });

  if (spec.layer) {
    spec.layer.forEach(layerSpec => {
      extractChartFields(layerSpec).forEach(f => {
        if (!fields.some(existing => existing.field === f.field)) {
          fields.push(f);
        }
      });
    });
  }
  return fields;
};

/**
 * Shared filter bar rendered above delivery components.
 */
const FilterBar = ({ columns, uniqueValues, filters, onFilterChange, onClearAll }) => {
  const activeCount = Object.values(filters).filter(v => v && v.length > 0).length;

  return (
    <div style={{
      display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '8px',
      padding: '6px 8px', background: '#fafafa', borderRadius: '4px',
      border: '1px solid #f0f0f0', alignItems: 'center'
    }}>
      <span style={{
        fontSize: '0.8em', color: '#8c8c8c', display: 'flex',
        alignItems: 'center', gap: '4px', whiteSpace: 'nowrap', flexShrink: 0
      }}>
        <FilterOutlined style={{ fontSize: '11px' }} />
        {activeCount > 0 ? `Filters (${activeCount})` : 'Filters'}:
      </span>
      {columns.map((col, idx) => (
        <Select
          key={col + '-' + idx}
          mode="multiple"
          placeholder={col}
          value={filters[idx] || []}
          options={(uniqueValues[idx] || []).map(v => ({ label: v, value: v }))}
          onChange={(values) => onFilterChange(idx, values)}
          style={{ minWidth: 100, maxWidth: 180 }}
          allowClear
          showSearch
          size="small"
          maxTagCount={1}
          maxTagPlaceholder={(omitted) => `+${omitted.length}`}
          popupMatchSelectWidth={false}
        />
      ))}
      {activeCount > 0 && (
        <Button
          size="small"
          icon={<ClearOutlined style={{ fontSize: '11px' }} />}
          onClick={onClearAll}
          type="text"
          style={{ fontSize: '0.78em', color: '#8c8c8c', padding: '0 4px' }}
        >
          Clear
        </Button>
      )}
    </div>
  );
};

/**
 * FilterableTable — data table using Ant Design Table with built-in column filters.
 *
 * @param {Object} data - { columns: string[], rows: any[][], row_count?, truncated? }
 * @param {number} maxRows - Maximum visible rows (default 50)
 * @param {boolean} fill - When true, removes maxHeight constraint (for dashboard cards)
 */
export const FilterableTable = ({ data, maxRows = 50, fill = false }) => {

  // Filter dropdown options are computed from the rows actually rendered
  // (the visible slice), so users never see a value that won't appear in
  // any rendered row. This avoids the "filter to value → empty table" UX trap.
  const visibleRows = useMemo(() => (data?.rows ? data.rows.slice(0, maxRows) : []), [data?.rows, maxRows]);
  const uniqueValues = useMemo(() => {
    return getTableColumnOptions(data?.columns, visibleRows);
  }, [data?.columns, visibleRows]);

  const antColumns = useMemo(() => {
    if (!data?.columns) return [];
    return data.columns.map((col, idx) => {
      const filterOpts = (uniqueValues[idx] || []).map(v => ({ text: v, value: v }));
      return {
        title: col,
        dataIndex: String(idx),
        key: col,
        ellipsis: true,
        filters: filterOpts.length <= 100 ? filterOpts : undefined,
        filterSearch: filterOpts.length > 10,
        onFilter: (value, record) => {
          const cell = record[String(idx)] != null ? String(record[String(idx)]) : '(null)';
          return cell === value;
        },
        render: (val) =>
          val === null ? <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>null</span> : String(val),
      };
    });
  }, [data?.columns, uniqueValues]);

  const antDataSource = useMemo(() => {
    return visibleRows.map((row, rowIdx) => {
      const record = { key: rowIdx };
      row.forEach((cell, cellIdx) => { record[String(cellIdx)] = cell; });
      return record;
    });
  }, [visibleRows]);

  if (!data?.columns || !data?.rows) return null;

  const totalRows = data.row_count || data.rows.length;

  return (
    <div style={{ fontSize: '0.85em', color: '#666', background: '#fff', borderRadius: '4px', overflow: 'auto', ...(fill ? { height: '100%', display: 'flex', flexDirection: 'column' } : { maxHeight: '450px' }) }}>
      <div style={{ marginBottom: '4px', color: '#999', fontSize: '0.9em', padding: '4px 0' }}>
        {`${totalRows} rows × ${data.columns.length} columns`}
        {data.truncated && ` (showing first ${data.rows.length} rows)`}
      </div>
      <div style={fill ? { flex: 1, overflow: 'auto', minHeight: 0 } : undefined}>
        <Table
          columns={antColumns}
          dataSource={antDataSource}
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          bordered
          style={{ fontSize: '0.9em' }}
        />
      </div>
      {data.rows.length > maxRows && (
        <div style={{ marginTop: '4px', color: '#8c8c8c', fontSize: '0.85em', textAlign: 'center', flexShrink: 0 }}>
          Showing first {maxRows} of {totalRows} rows
        </div>
      )}
    </div>
  );
};

/**
 * FilterableChart — Vega-Lite chart with auto-generated per-field filters.
 *
 * Extracts fields from the spec's encoding channels and builds filter
 * drop-downs from the embedded data (or falls back to tableData).
 *
 * @param {Object|string} chartSpec - Vega-Lite specification
 * @param {Object}        tableData - Optional fallback { columns, rows }
 * @param {boolean}       fill      - When true, chart fills its container (for dashboard cards)
 */
export const FilterableChart = ({ chartSpec, tableData, fill = false }) => {
  const [filters, setFilters] = useState({});
  const chartContainerRef = useRef(null);
  const [resizeKey, setResizeKey] = useState(0);

  // Track container resizes so chart re-renders at new dimensions
  useEffect(() => {
    if (!fill) return;
    const el = chartContainerRef.current;
    if (!el) return;
    let timer;
    const ro = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(() => setResizeKey(k => k + 1), 200);
    });
    ro.observe(el);
    return () => { ro.disconnect(); clearTimeout(timer); };
  }, [fill]);

  const parsedSpec = useMemo(() => {
    if (!chartSpec) return null;
    try {
      return typeof chartSpec === 'string' ? JSON.parse(chartSpec) : chartSpec;
    } catch {
      return null;
    }
  }, [chartSpec]);

  const fields = useMemo(() => extractChartFields(parsedSpec), [parsedSpec]);
  const fieldNames = useMemo(() => fields.map(f => f.field), [fields]);

  const dataValues = useMemo(() => {
    if (parsedSpec?.data?.values && Array.isArray(parsedSpec.data.values)) {
      return parsedSpec.data.values;
    }
    if (tableData?.columns && tableData?.rows) {
      return tableData.rows.map(row => {
        const obj = {};
        tableData.columns.forEach((col, idx) => { obj[col] = row[idx]; });
        return obj;
      });
    }
    return [];
  }, [parsedSpec, tableData]);

  const uniqueValues = useMemo(() => {
    const opts = {};
    fieldNames.forEach((field, idx) => {
      const valueSet = new Set();
      for (const row of dataValues) {
        if (valueSet.size >= 100) break;
        valueSet.add(row[field] != null ? String(row[field]) : '(null)');
      }
      opts[idx] = [...valueSet].sort((a, b) => {
        const numA = Number(a);
        const numB = Number(b);
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
        return a.localeCompare(b);
      });
    });
    return opts;
  }, [fieldNames, dataValues]);

  // Render / re-render chart whenever spec or filters change
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el || !parsedSpec) return;

    const active = Object.entries(filters).filter(([_, v]) => v && v.length > 0);

    let spec;
    if (active.length === 0) {
      spec = parsedSpec;
    } else {
      const filteredValues = dataValues.filter(row =>
        active.every(([fIdx, values]) => {
          const field = fieldNames[Number(fIdx)];
          const cell = row[field] != null ? String(row[field]) : '(null)';
          return values.includes(cell);
        })
      );
      spec = { ...parsedSpec, data: { values: filteredValues } };
    }

    // If data is not embedded in spec, inject it
    if (!spec.data?.values && dataValues.length > 0) {
      spec = { ...spec, data: { values: active.length === 0 ? dataValues : spec.data?.values || dataValues } };
    }

    // In fill mode, use container-responsive sizing
    if (fill) {
      spec = { ...spec, width: 'container', height: 'container', autosize: { type: 'fit', contains: 'padding' } };
    }

    vegaEmbed(el, spec, {
      actions: { export: true, source: false, compiled: false, editor: false }
    }).catch(err => {
      if (el) el.innerHTML = `<div style="color:#ff4d4f;padding:12px">Chart error: ${err.message}</div>`;
    });
  }, [parsedSpec, dataValues, filters, fieldNames, fill, resizeKey]);

  const handleFilterChange = useCallback((idx, values) => {
    setFilters(prev => ({ ...prev, [idx]: values }));
  }, []);
  const handleClearAll = useCallback(() => setFilters({}), []);

  return (
    <div style={fill
      ? { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
      : { background: '#fff', padding: '12px', borderRadius: '4px', overflow: 'auto' }
    }>
      {fields.length > 0 && dataValues.length > 0 && (
        <FilterBar
          columns={fieldNames}
          uniqueValues={uniqueValues}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearAll={handleClearAll}
        />
      )}
      <div ref={chartContainerRef} style={fill ? { flex: 1, minHeight: 0 } : { minHeight: '300px' }} />
    </div>
  );
};
